import React, { useEffect, useState } from "react";
import { Plus, QrCode as QrIcon, Download, X } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function QRCodes() {
  const [codes, setCodes] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => api.get("/qr").then(({ data }) => setCodes(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const downloadPng = (c) => { const a = document.createElement("a"); a.href = `data:image/png;base64,${c.png}`; a.download = `${c.name}.png`; a.click(); };
  const downloadSvg = (c) => {
    const token = localStorage.getItem("alto_token");
    fetch(`${API}/qr/${c.id}/svg`, { headers: { Authorization: `Bearer ${token}`, "X-Org-Id": localStorage.getItem("alto_org") } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${c.name}.svg`; a.click(); });
  };

  return (
    <div>
      <PageHeader title="QR Codes" subtitle="Trackable QR codes with redirect URLs — change the destination without reprinting.">
        <RedButton onClick={() => setShow(true)} data-testid="qr-create"><Plus size={15} /> Create QR Code</RedButton>
      </PageHeader>

      {codes.length === 0 ? (
        <Card className="p-6"><Empty icon={QrIcon} title="No QR codes yet" hint="Generate a trackable QR code for a form, event, or external URL." action={<RedButton onClick={() => setShow(true)}><Plus size={15} /> Create QR Code</RedButton>} /></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {codes.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`qr-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-display font-bold text-slate-900">{c.name}</h3><p className="text-xs text-slate-500 mt-0.5">{c.destination_type} · {c.source}</p></div>
                <Badge tone="blue">{c.scan_count || 0} scans</Badge>
              </div>
              <img src={`data:image/png;base64,${c.png}`} alt={c.name} className="w-32 h-32 mx-auto my-4" />
              <p className="text-xs text-slate-400 text-center break-all mb-3">{c.redirect_url}</p>
              <div className="flex gap-2">
                <GhostButton className="flex-1 !py-2" onClick={() => downloadPng(c)} data-testid={`qr-png-${c.id}`}><Download size={13} /> PNG</GhostButton>
                <GhostButton className="flex-1 !py-2" onClick={() => downloadSvg(c)} data-testid={`qr-svg-${c.id}`}><Download size={13} /> SVG</GhostButton>
              </div>
            </Card>
          ))}
        </div>
      )}
      {show && <QRModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function QRModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", destination_type: "External URL", destination: "", source: "QR Code", channel: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name || !form.destination) { toast.error("Name and destination required"); return; }
    try { await api.post("/qr", form); toast.success("QR code created"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-xl font-bold text-slate-900">Create QR Code</h2><button onClick={onClose} className="text-slate-400"><X size={20} /></button></div>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="qr-name" placeholder="e.g. Outreach Downtown" /></Field>
          <Field label="Destination type"><select className={inputCls} value={form.destination_type} onChange={set("destination_type")} data-testid="qr-type">{["External URL","CRM Form","Event","Landing Page","Campaign"].map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Destination URL"><input className={inputCls} value={form.destination} onChange={set("destination")} data-testid="qr-destination" placeholder="https://… or /f/org/form" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source"><input className={inputCls} value={form.source} onChange={set("source")} data-testid="qr-source" /></Field>
            <Field label="Channel"><input className={inputCls} value={form.channel} onChange={set("channel")} data-testid="qr-channel" placeholder="Flyer, poster…" /></Field>
          </div>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton><RedButton className="flex-1" onClick={save} data-testid="qr-save">Generate</RedButton></div>
      </Card>
    </div>
  );
}
